import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const SCREENS = ['Dashboard', 'Calculadora de Lotes', 'Gestion de Lotes', 'Inventario', 'Analisis de Ventas', 'Datos de Mercado', 'Configuracion', 'Preferencias', 'Ayuda', 'Otro'];

const CHANGELOG = [
  { version: 'v1.3.0', date: '2026-03-04', summary: 'Pantalla de Ayuda con guia de usuario, referencia de formulas y soporte integrado.' },
  { version: 'v1.2.0', date: '2026-03-04', summary: 'Preferencias de usuario: perfil, pantalla, dashboard, notificaciones y motor de precios.' },
  { version: 'v1.1.0', date: '2026-03-04', summary: 'Datos de Mercado en tiempo real con suscripcion Supabase, graficos de tendencia y entrada manual.' },
  { version: 'v1.0.0', date: '2026-03-04', summary: 'Lanzamiento inicial: Dashboard, Calculadora, Lotes, Inventario y Analisis de Ventas.' },
];

function BugReportForm() {
  const { user } = useAuth();
  const [form, setForm] = useState({ screen: '', description: '', severity: 'low' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.screen || !form?.description?.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const { error: err } = await supabase?.from('support_tickets')?.insert({ user_id: user?.id, screen: form?.screen, description: form?.description?.trim(), severity: form?.severity, status: 'open' });
      if (err) throw err;
      setSubmitted(true);
      setForm({ screen: '', description: '', severity: 'low' });
    } catch (err) {
      setError(err?.message || 'Error al enviar el reporte.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-lg p-5 flex items-start gap-4" style={{ background: 'color-mix(in srgb, var(--color-success) 10%, var(--color-card))', border: '1px solid color-mix(in srgb, var(--color-success) 30%, transparent)' }}>
        <Icon name="CheckCircle" size={22} color="var(--color-success)" strokeWidth={2} className="flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold" style={{ fontSize: '0.9375rem', fontFamily: '"IBM Plex Sans", sans-serif', color: 'var(--color-foreground)' }}>Reporte enviado correctamente</p>
          <p className="mt-1" style={{ fontSize: '0.875rem', fontFamily: '"IBM Plex Sans", sans-serif', color: 'var(--color-muted-foreground)' }}>Gracias por tu reporte. Nuestro equipo lo revisara en breve.</p>
          <button className="mt-3 text-sm font-medium transition-standard hover:opacity-80" style={{ color: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif' }} onClick={() => setSubmitted(false)}>
            Enviar otro reporte
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif', color: 'var(--color-foreground)' }}>
          Pantalla afectada <span style={{ color: 'var(--color-error)' }}>*</span>
        </label>
        <select
          value={form?.screen}
          onChange={(e) => setForm((f) => ({ ...f, screen: e?.target?.value }))}
          required
          className="w-full rounded-md px-3 py-2.5 border text-sm transition-standard focus:outline-none"
          style={{ background: 'var(--color-background)', borderColor: 'var(--color-border)', color: 'var(--color-foreground)', fontFamily: '"IBM Plex Sans", sans-serif' }}
        >
          <option value="">Selecciona una pantalla...</option>
          {SCREENS?.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif', color: 'var(--color-foreground)' }}>
          Descripcion del problema <span style={{ color: 'var(--color-error)' }}>*</span>
        </label>
        <textarea
          value={form?.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e?.target?.value }))}
          required
          rows={4}
          placeholder="Describe el problema con el mayor detalle posible: que hiciste, que esperabas y que ocurrio..."
          className="w-full rounded-md px-3 py-2.5 border text-sm transition-standard focus:outline-none resize-none"
          style={{ background: 'var(--color-background)', borderColor: 'var(--color-border)', color: 'var(--color-foreground)', fontFamily: '"IBM Plex Sans", sans-serif' }}
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-2" style={{ fontFamily: '"IBM Plex Sans", sans-serif', color: 'var(--color-foreground)' }}>Severidad</label>
        <div className="flex gap-3">
          {[
            { value: 'low', label: 'Baja', color: 'var(--color-success)' },
            { value: 'medium', label: 'Media', color: 'var(--color-warning)' },
            { value: 'high', label: 'Alta', color: 'var(--color-error)' },
          ]?.map((opt) => (
            <label
              key={opt?.value}
              className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-md border transition-standard"
              style={{
                borderColor: form?.severity === opt?.value ? opt?.color : 'var(--color-border)',
                background: form?.severity === opt?.value ? `color-mix(in srgb, ${opt?.color} 10%, var(--color-card))` : 'var(--color-card)',
              }}
            >
              <input type="radio" name="severity" value={opt?.value} checked={form?.severity === opt?.value} onChange={(e) => setForm((f) => ({ ...f, severity: e?.target?.value }))} className="sr-only" />
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: opt?.color }} />
              <span className="text-sm font-medium" style={{ fontFamily: '"IBM Plex Sans", sans-serif', color: 'var(--color-foreground)' }}>{opt?.label}</span>
            </label>
          ))}
        </div>
      </div>
      {error && <p className="text-sm" style={{ color: 'var(--color-error)', fontFamily: '"IBM Plex Sans", sans-serif' }}>{error}</p>}
      <button
        type="submit"
        disabled={submitting || !form?.screen || !form?.description?.trim()}
        className="flex items-center gap-2 px-5 py-2.5 rounded-md font-medium text-sm transition-standard hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ background: 'var(--color-accent)', color: '#fff', fontFamily: '"IBM Plex Sans", sans-serif' }}
      >
        {submitting
          ? <><Icon name="Loader2" size={16} color="#fff" strokeWidth={2} className="animate-spin" /> Enviando...</>
          : <><Icon name="Send" size={16} color="#fff" strokeWidth={2} /> Enviar reporte</>
        }
      </button>
    </form>
  );
}

function FeedbackWidget() {
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!rating) return;
    setSubmitting(true);
    setError(null);
    try {
      const { error: err } = await supabase?.from('feedback')?.insert({ user_id: user?.id, rating, comment: comment?.trim() });
      if (err) throw err;
      setSubmitted(true);
    } catch (err) {
      setError(err?.message || 'Error al enviar la valoracion.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex items-center gap-3">
        <Icon name="Heart" size={20} color="var(--color-accent)" strokeWidth={2} />
        <p style={{ fontSize: '0.9rem', fontFamily: '"IBM Plex Sans", sans-serif', color: 'var(--color-foreground)' }}>Gracias por tu valoracion! Tu opinion nos ayuda a mejorar.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setRating(star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            className="transition-standard hover:scale-110"
            aria-label={`${star} estrella`}
          >
            <Icon
              name="Star"
              size={28}
              color={(hovered || rating) >= star ? '#f59e0b' : 'var(--color-border)'}
              strokeWidth={2}
              style={{ fill: (hovered || rating) >= star ? '#f59e0b' : 'transparent', transition: 'all 0.15s' }}
            />
          </button>
        ))}
        {rating > 0 && (
          <span className="ml-2 text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: '"IBM Plex Sans", sans-serif' }}>
            {['', 'Muy malo', 'Malo', 'Regular', 'Bueno', 'Excelente']?.[rating]}
          </span>
        )}
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e?.target?.value)}
        rows={3}
        placeholder="Comentario opcional..."
        className="w-full rounded-md px-3 py-2.5 border text-sm transition-standard focus:outline-none resize-none"
        style={{ background: 'var(--color-background)', borderColor: 'var(--color-border)', color: 'var(--color-foreground)', fontFamily: '"IBM Plex Sans", sans-serif' }}
      />
      {error && <p className="text-sm" style={{ color: 'var(--color-error)', fontFamily: '"IBM Plex Sans", sans-serif' }}>{error}</p>}
      <button
        onClick={handleSubmit}
        disabled={!rating || submitting}
        className="flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-standard hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ background: 'var(--color-accent)', color: '#fff', fontFamily: '"IBM Plex Sans", sans-serif' }}
      >
        {submitting
          ? <><Icon name="Loader2" size={15} color="#fff" strokeWidth={2} className="animate-spin" /> Enviando...</>
          : <><Icon name="Star" size={15} color="#fff" strokeWidth={2} /> Enviar valoracion</>
        }
      </button>
    </div>
  );
}

export default function SupportFeedbackTab() {
  return (
    <div className="space-y-8">
      <div className="rounded-lg p-5 flex items-start gap-4 border" style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
        <div className="flex-shrink-0 flex items-center justify-center rounded-lg" style={{ width: 44, height: 44, background: 'color-mix(in srgb, var(--color-accent) 12%, transparent)' }}>
          <Icon name="Mail" size={22} color="var(--color-accent)" strokeWidth={2} />
        </div>
        <div>
          <h4 className="font-semibold text-foreground" style={{ fontSize: '0.9375rem', fontFamily: 'var(--font-heading)' }}>Contactar soporte</h4>
          <p className="mt-1 text-muted-foreground" style={{ fontSize: '0.875rem', fontFamily: '"IBM Plex Sans", sans-serif' }}>
            Para consultas urgentes o asistencia personalizada, escribenos a{' '}
            <a href="mailto:soporte@metalrecovery.app" className="font-medium transition-standard hover:opacity-80" style={{ color: 'var(--color-accent)' }}>soporte@metalrecovery.app</a>
          </p>
          <p className="mt-1 text-muted-foreground" style={{ fontSize: '0.8125rem', fontFamily: '"IBM Plex Sans", sans-serif' }}>Tiempo de respuesta habitual: 24-48 horas laborables.</p>
        </div>
      </div>
      <div>
        <h3 className="font-semibold text-foreground mb-4" style={{ fontSize: '1rem', fontFamily: 'var(--font-heading)', letterSpacing: '-0.01em' }}>Reportar un problema</h3>
        <div className="rounded-lg border p-5" style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
          <BugReportForm />
        </div>
      </div>
      <div>
        <h3 className="font-semibold text-foreground mb-4" style={{ fontSize: '1rem', fontFamily: 'var(--font-heading)', letterSpacing: '-0.01em' }}>Valorar la aplicacion</h3>
        <div className="rounded-lg border p-5" style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
          <FeedbackWidget />
        </div>
      </div>
      <div>
        <h3 className="font-semibold text-foreground mb-4" style={{ fontSize: '1rem', fontFamily: 'var(--font-heading)', letterSpacing: '-0.01em' }}>Historial de cambios</h3>
        <div className="rounded-lg border overflow-hidden" style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
          {CHANGELOG?.map((entry, i) => (
            <div key={entry?.version} className="flex items-start gap-4 px-5 py-4" style={{ borderBottom: i < CHANGELOG?.length - 1 ? '1px solid var(--color-border)' : 'none' }}>
              <div className="flex-shrink-0 pt-0.5">
                <span
                  className="inline-block px-2 py-0.5 rounded font-mono font-semibold text-xs"
                  style={{ background: i === 0 ? 'var(--color-accent)' : 'var(--color-muted)', color: i === 0 ? '#fff' : 'var(--color-muted-foreground)', fontFamily: '"IBM Plex Mono", monospace' }}
                >
                  {entry?.version}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-foreground" style={{ fontSize: '0.875rem', fontFamily: '"IBM Plex Sans", sans-serif', lineHeight: 1.5 }}>{entry?.summary}</p>
                <p className="mt-0.5 text-muted-foreground" style={{ fontSize: '0.75rem', fontFamily: '"IBM Plex Sans", sans-serif' }}>{entry?.date}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
